import { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import {
  SortParams,
  InvestorSpaceModel,
  PagingModel,
  State,
  SortParamsArg,
} from 'models';

import services from 'services';

import { SpacesAddonDetails } from 'components';

interface SpaceAddonState {
  investorSpaces: InvestorSpaceModel[];
  sortParams: SortParams;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  loading: boolean;
  error: boolean;
  activeTab: string;
  refresh: boolean;
}

const SpacesAddon = () => {
  const { coopId } = useSelector((state: State) => ({
    coopId: state.settings.current_cooperative,
  }));
  const [state, setState] = useState<SpaceAddonState>({
    investorSpaces: [],
    sortParams: {
      order: 'asc',
      orderBy: 'uds_name',
      type: 'string',
    },
    loading: false,
    error: false,
    pageNumber: 1,
    pageSize: 3,
    totalCount: 0,
    activeTab: '100000000',
    refresh: false,
  });

  useEffect(() => {
    fetchSpaces(
      state.pageNumber,
      state.pageSize,
      state.sortParams,
      state.activeTab
    );
  }, [
    state.pageNumber,
    state.pageSize,
    state.sortParams,
    state.activeTab,
    coopId,
  ]);

  const fetchSpaces = async (
    pageNumber: number,
    pageCount: number,
    sortParams: SortParams,
    activeTab: string
  ) => {
    try {
      setState((prevState) => ({ ...prevState, loading: true, error: false }));

      const searchParams = `requestSettings.pageNumber=${pageNumber}&requestSettings.pageCount=${pageCount}&filterSettings.filters=uds_spacestatuscode:opt:${activeTab}&requestSettings.sort=${sortParams.orderBy}:${sortParams.order}`;

      const investorRes: {
        Data: InvestorSpaceModel[];
        Paging: PagingModel;
      } = (
        await services.get(`/api/portfolios/${coopId}/spaces?${searchParams}`)
      ).data;

      setState((prevState) => ({
        ...prevState,
        investorSpaces: investorRes.Data,
        totalCount: investorRes.Paging.TotalCount || 0,
        loading: false,
      }));
    } catch (err) {
      console.error(err);

      setState((prevState) => ({ ...prevState, loading: false, error: true }));
    }
  };

  const handleChangeSortParams = useCallback((params: SortParamsArg) => {
    setState((prevState) => ({
      ...prevState,
      sortParams: {
        ...prevState.sortParams,
        order: prevState.sortParams.order === 'asc' ? 'desc' : 'asc',
        orderBy: params.id,
        type: params.type,
      },
    }));
  }, []);

  const handleChangeTab = useCallback((id: string) => {
    setState((prevState) => ({ ...prevState, activeTab: id }));
  }, []);

  const handleChangeCurrentPage = useCallback(
    (e: ChangeEvent<unknown>, page: number) => {
      setState((prevState) => ({
        ...prevState,
        pageNumber: page,
      }));
    },
    []
  );

  return (
    <SpacesAddonDetails
      {...state}
      handleChangeTab={handleChangeTab}
      handleChangeCurrentPage={handleChangeCurrentPage}
      handleChangeSortParams={handleChangeSortParams}
    />
  );
};

export default SpacesAddon;
